import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useProfile } from '../context/ProfileContext';
import { achievementsAPI, pointsAPI } from '../services/api';

const AVATARS = ['🧑‍🚀', '🦸', '🧙', '🧜', '🦊', '🐼', '🦄', '🌟'];

const getLevelTitle = (level) => {
  if (level < 3) return 'Space Cadet';
  if (level < 5) return 'Star Explorer';
  if (level < 8) return 'Galaxy Hero';
  return 'Universe Master';
};

export default function ProfileScreen() {
  const navigate = useNavigate();
  const { profile, updateProfile } = useProfile();
  const [achievements, setAchievements] = useState([]);
  const [recentPoints, setRecentPoints] = useState([]);
  const [editing, setEditing] = useState(false);
  const [name, setName] = useState('');

  useEffect(() => {
    if (profile) {
      setName(profile.name);
      achievementsAPI.getAll(profile.id).then(setAchievements).catch(() => {});
      pointsAPI.getLog(profile.id).then(setRecentPoints).catch(() => {});
    }
  }, [profile]);

  const handleSave = async () => {
    if (!name.trim()) return;
    await updateProfile({ name: name.trim() });
    setEditing(false);
  };

  if (!profile) return <div className="loading-screen"><div className="spinner" /></div>;

  return (
    <div className="screen">
      <div className="screen-header" style={{ background: 'linear-gradient(135deg, #667eea, #764ba2)', paddingBottom: 30 }}>
        <button className="btn-back" onClick={() => navigate('/')} style={{ background: 'rgba(255,255,255,0.2)', color: 'white' }}>←</button>
        <h1 style={{ fontFamily: 'var(--font-title)', color: 'white', fontSize: '1.4rem', flex: 1 }}>My Profile</h1>
      </div>

      <div className="screen-body" style={{ paddingTop: 0, marginTop: -20 }}>
        {/* Avatar Card */}
        <div className="card" style={{ textAlign: 'center', marginBottom: 16 }}>
          <div style={{ fontSize: '4rem', marginBottom: 8 }}>🧑‍🚀</div>
          {editing ? (
            <div>
              <input className="input" value={name} onChange={e => setName(e.target.value)} style={{ textAlign: 'center', fontWeight: 800, marginBottom: 10 }} />
              <div style={{ display: 'flex', gap: 8 }}>
                <button className="btn btn-success" style={{ flex: 1 }} onClick={handleSave}>✅ Save</button>
                <button className="btn" style={{ flex: 1, background: 'var(--bg)' }} onClick={() => setEditing(false)}>Cancel</button>
              </div>
            </div>
          ) : (
            <div>
              <h2 style={{ fontFamily: 'var(--font-title)', fontSize: '1.4rem' }}>{profile.name}</h2>
              <div style={{ color: 'var(--purple)', fontWeight: 700 }}>Level {profile.level} · {getLevelTitle(profile.level)}</div>
              <button className="btn btn-sm" style={{ marginTop: 10, background: 'var(--bg)' }} onClick={() => setEditing(true)}>✏️ Edit Name</button>
            </div>
          )}
        </div>

        {/* Stats */}
        <div className="grid-2" style={{ marginBottom: 16 }}>
          <div className="card" style={{ textAlign: 'center' }}>
            <div style={{ fontFamily: 'var(--font-title)', fontSize: '1.8rem', color: 'var(--yellow)' }}>⭐ {profile.total_points || 0}</div>
            <div style={{ color: 'var(--gray)', fontSize: '0.8rem' }}>Total Points</div>
          </div>
          <div className="card" style={{ textAlign: 'center' }}>
            <div style={{ fontFamily: 'var(--font-title)', fontSize: '1.8rem', color: 'var(--purple)' }}>🏆 {achievements.length}</div>
            <div style={{ color: 'var(--gray)', fontSize: '0.8rem' }}>Achievements</div>
          </div>
        </div>

        {/* Level Progress */}
        <div className="card" style={{ marginBottom: 16 }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', fontWeight: 800, marginBottom: 8 }}>
            <span>Level {profile.level}</span>
            <span style={{ color: 'var(--gray)', fontWeight: 400, fontSize: '0.85rem' }}>{profile.total_points % 100}/100 to next</span>
          </div>
          <div className="progress-bar">
            <div className="progress-bar-fill" style={{ width: `${profile.total_points % 100}%` }} />
          </div>
        </div>

        {/* Achievements */}
        {achievements.length > 0 && (
          <div style={{ marginBottom: 16 }}>
            <h3 style={{ fontFamily: 'var(--font-title)', marginBottom: 10 }}>🏅 Achievements</h3>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
              {achievements.map(a => (
                <div key={a.id} className="card" style={{ display: 'flex', gap: 12, padding: 14 }}>
                  <div style={{ fontSize: '2rem' }}>{a.icon}</div>
                  <div>
                    <div style={{ fontWeight: 800 }}>{a.achievement_name}</div>
                    <div style={{ color: 'var(--gray)', fontSize: '0.8rem' }}>{a.description}</div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Recent Activity */}
        {recentPoints.length > 0 && (
          <div>
            <h3 style={{ fontFamily: 'var(--font-title)', marginBottom: 10 }}>⚡ Recent Activity</h3>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
              {recentPoints.slice(0, 8).map(p => (
                <div key={p.id} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '10px 14px', background: 'var(--bg)', borderRadius: 12 }}>
                  <span style={{ fontSize: '0.85rem', fontWeight: 600 }}>{p.module} · {p.reason}</span>
                  <span style={{ color: 'var(--green)', fontWeight: 800 }}>+{p.points} ⭐</span>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
